import type { Ref } from 'vue'
import type { Map as LeafletMap } from 'leaflet'
import type { LMap } from '@vue-leaflet/vue-leaflet'
import SearchResultsMapClass from '~/utils/maps/SearchResultsMap'
import type MapMarkerEventData from '~/types/search/MapMarkerEventData'
import { useSearchParamsStore } from '~~/stores/searchParams'
import { useSearchResultsMapStore } from '~~/stores/searchResultsMap'
import useSearchResultsMapOptions from '~/composables/search/useSearchResultsMapOptions'
import useSearchResultsMapMarkers from '~/composables/search/useSearchResultsMapMarkers'
import useSearchResultsMoveendSearch from '~/composables/search/useSearchResultsMoveendSearch'
import { SEARCH_DATA_REF_KEY } from '~/composables/search/useSearchResultsMapKeys'

const useSearchResultsMapCore = (
  map: Ref<typeof LMap>,
  props: { data: any; pending: boolean },
  markerEventsHandler: (eventName: string, data: MapMarkerEventData) => void,
  isMoveendActive: Ref<boolean>
) => {
  const { $gtmModules } = useNuxtApp()
  const searchParamsStore = useSearchParamsStore()
  const mapStore = useSearchResultsMapStore()

  // Inject the raw useFetch data ref provided by SearchPage.
  // Watchers on this ref MUST be created during setup() (not in async callbacks like
  // @ready/initMap) — Vue only reliably tracks cross-component reactive deps in setup().
  const injectedDataRef = inject(SEARCH_DATA_REF_KEY, null)

  const { mapSettings, mapOptions } = useSearchResultsMapOptions()

  const searchResultsMap = shallowRef<SearchResultsMapClass | null>(null)
  const mapContainerRef = ref<HTMLElement | null>(null)

  const { updateMarkers, registerWatchers, stopWatchers: markerWatchStoppers } = useSearchResultsMapMarkers(
    searchResultsMap,
    props,
    injectedDataRef
  )

  const { registerHandlers, skipZoomOnNextLimitsChange, cleanupTimers } = useSearchResultsMoveendSearch(
    map,
    mapContainerRef,
    isMoveendActive,
    searchResultsMap,
    mapSettings
  )

  // All watcher stop-handles — populated here (setup level) AND in initMap
  const watchStoppers: Array<() => void> = []

  // ── Setup-level watcher for search results ──────────────────────────────
  // Registered HERE (in setup) so Vue's reactivity system tracks it correctly
  // across component remounts. Covers both modal (SearchResultsMapModal) and
  // inline (SearchResultsMap) maps — SearchPage always provides SEARCH_DATA_REF_KEY.
  if (!injectedDataRef) {
    throw new Error('useSearchResultsMapCore: SEARCH_DATA_REF_KEY not provided. Ensure SearchPage provides it.')
  }
  watchStoppers.push(watch(injectedDataRef, () => {
    if (!mapStore.search || !searchResultsMap.value || !map.value?.leafletObject) {
      return
    }
    searchResultsMap.value.clearMarkers()
    updateMarkers()
    if (searchParamsStore.limits === '') {
      mapStore.moveMapSearch = false
      searchResultsMap.value.zoomMap(searchParamsStore.limits)
    }
    mapStore.isSpin = false
    setTimeout(() => {
      mapStore.search = true
    }, 300)
  }))

  const initMap = (mapContainer: HTMLElement) => {
    mapContainerRef.value = mapContainer

    const { currentPortal } = usePortals()
    const envelope = (currentPortal.value as any)?.portal?.envelope

    searchResultsMap.value = new SearchResultsMapClass(
      map.value.leafletObject,
      envelope,
      markerEventsHandler
    )

    const leafletMap = map.value.leafletObject as LeafletMap

    leafletMap.on('click', () => {
      $gtmModules.mapsModule.sendMapClickEvent()
    })

    let oldZoom = leafletMap.getZoom()
    leafletMap.on('zoomend', () => {
      if (searchResultsMap.value && !searchResultsMap.value.isAnimatingZoom) {
        const newZoom = leafletMap.getZoom()
        if (newZoom > oldZoom) {
          $gtmModules.mapsModule.sendMapZoomInEvent()
        } else {
          $gtmModules.mapsModule.sendMapZoomOutEvent()
        }
        oldZoom = newZoom
      }
    })

    if (props.pending === false && map.value?.leafletObject && searchResultsMap.value) {
      updateMarkers()
      // Map uses store limits and falls back to envelope if empty
      searchResultsMap.value.zoomMap(searchParamsStore.limits)
    }

    // Register marker hover/highlight watchers
    registerWatchers()

    // Register moveend and isSpin handlers
    registerHandlers()

    const watchOrt = computed(() => searchParamsStore.ort)
    watchStoppers.push(watch(watchOrt, () => {
      searchParamsStore.limits = ''
    }))

    watchStoppers.push(watch(() => searchParamsStore.limits, () => {
      if (!map.value?.leafletObject || !searchResultsMap.value) {
        return
      }
      // Skip zoom when user set the limits (prevents loop)
      if (skipZoomOnNextLimitsChange.value) {
        skipZoomOnNextLimitsChange.value = false
        return
      }
      mapStore.moveMapSearch = false
      // Set flag BEFORE zoomMap so the resulting flyToBounds moveend is swallowed
      skipZoomOnNextLimitsChange.value = true
      searchResultsMap.value.zoomMap(searchParamsStore.limits)
    }))
  }

  const cleanup = () => {
    // Cleanup timers and moveend/isSpin watchers
    cleanupTimers()

    // Cleanup marker watchers
    markerWatchStoppers.forEach(stop => stop())
    markerWatchStoppers.length = 0

    // Cleanup core watchers (ort, limits, data)
    watchStoppers.forEach(stop => stop())
    watchStoppers.length = 0

    if (searchResultsMap.value) {
      searchResultsMap.value.destroy()
      searchResultsMap.value = null
    }
  }

  return {
    mapSettings,
    mapOptions,
    initMap,
    cleanup,
    getSearchResultsMap: () => searchResultsMap.value
  }
}

export default useSearchResultsMapCore
